import {
  backupCheckoutCartCourses,
  clearCheckoutCartBackup,
  readCartCourses,
  readCheckoutCartBackup,
} from "./cartCourseSelection";
import { extractOwnedCourseIds, isCourseOwnedByUser } from "./ownedCourseIds";
import { getCurrentUserProfile } from "./profileCache";
import { CHECKOUT_OWNED_NOTICE_KEY } from "./sessionKeys";

/**
 * @typedef {{ _id?: string }} StoredCartCourse
 */

/**
 * @param {StoredCartCourse[]} courses
 * @param {string[]} ownedIds
 * @returns {StoredCartCourse[]}
 */
function withoutOwnedCourses(courses, ownedIds) {
  return courses.filter((curso) => !isCourseOwnedByUser(curso._id, ownedIds));
}

/**
 * @param {"all" | "some"} notice
 */
function persistOwnedNotice(notice) {
  try {
    sessionStorage.setItem(CHECKOUT_OWNED_NOTICE_KEY, notice);
  } catch {
    /* ignore storage failures */
  }
}

/**
 * Removes courses the signed-in student already owns from the local cart
 * and from the checkout backup so Pago cannot restore them after login.
 * @returns {Promise<{ kept: StoredCartCourse[], removedCount: number, ownedIds: string[] }>}
 */
export async function applyOwnedCoursesToStoredCart() {
  const profile = await getCurrentUserProfile({ force: true });
  const ownedIds = extractOwnedCourseIds(profile);
  const cart = readCartCourses();
  const backup = readCheckoutCartBackup();
  if (ownedIds.length === 0) {
    return { kept: cart, removedCount: 0, ownedIds };
  }

  const kept = withoutOwnedCourses(cart, ownedIds);
  const keptBackup = withoutOwnedCourses(backup, ownedIds);
  const removedFromCart = cart.length - kept.length;
  const removedFromBackup = backup.length - keptBackup.length;

  if (removedFromCart > 0) {
    localStorage.setItem("carrito", JSON.stringify(kept));
  }
  if (removedFromBackup > 0) {
    if (keptBackup.length === 0) {
      clearCheckoutCartBackup();
    } else {
      backupCheckoutCartCourses(keptBackup);
    }
  }

  const remaining = kept.length > 0 ? kept.length : keptBackup.length;
  if (removedFromCart > 0 || removedFromBackup > 0) {
    persistOwnedNotice(remaining === 0 ? "all" : "some");
  }

  return {
    kept,
    removedCount: Math.max(removedFromCart, removedFromBackup),
    ownedIds,
  };
}
